import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { Check, DollarSign, GraduationCap, School } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@/lib/utils'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group.tsx'

export type AwardRuleOptionKey =
  | 'grade_level'
  | 'education_type'
  | 'fixed_amount'

interface DistributionSettingsProps {
  initialSelected?: AwardRuleOptionKey
  onContinue: (selected: AwardRuleOptionKey) => void
  onCancel: () => void
}

interface OptionDef {
  key: AwardRuleOptionKey
  title: string
  description: string
  helper: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}

const OPTIONS: OptionDef[] = [
  {
    key: 'grade_level',
    title: 'Grade Level Configuration',
    description: 'Set different award amounts for each grade level (K-12)',
    helper: 'Use when scholarship amounts should vary based on student grade',
    icon: GraduationCap,
  },
  {
    key: 'education_type',
    title: 'Education Type',
    description:
      'Set different amounts based on homeschool, private school, public school, etc',
    helper: 'Use when scholarship amounts should vary by type of education',
    icon: School,
  },
  {
    key: 'fixed_amount',
    title: 'Fixed Dollar Amount',
    description: 'All eligible students receive the same scholarship amount.',
    helper: 'Use when you want equal distribution among all recipients',
    icon: DollarSign,
  },
]

const FormSchema = z.object({
  choice: z.enum(['grade_level', 'education_type', 'fixed_amount'], {
    required_error: 'Please choose one option.',
    invalid_type_error: 'Please choose one option.',
  }),
})
type FormValues = z.infer<typeof FormSchema>

export default function DistributionSettings({
  initialSelected,
  onContinue,
  onCancel,
}: DistributionSettingsProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: initialSelected ? { choice: initialSelected } : undefined,
  })

  function onSubmit(values: FormValues) {
    onContinue(values.choice)
  }

  function handleCancel() {
    reset()
    onCancel()
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full px-3 py-6 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardHeader>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Create Award Rule
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Define how scholarships will be calculated and distributed to
                students
              </p>
            </div>
          </CardHeader>

          <CardContent>
            <Controller
              name="choice"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value ?? ''}
                  onValueChange={(val) =>
                    field.onChange(
                      val === '' ? undefined : (val as AwardRuleOptionKey)
                    )
                  }
                  className="space-y-4"
                >
                  {OPTIONS.map((opt) => {
                    const selected = field.value === opt.key
                    const Icon = opt.icon
                    return (
                      <label
                        key={opt.key}
                        className={cn(
                          'relative block w-full cursor-pointer rounded-lg border p-5 text-left transition-colors',
                          'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-offset-1 focus-within:ring-orange-400',
                          selected
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-200 bg-white'
                        )}
                      >
                        <div className="absolute right-3 top-3">
                          <RadioGroupItem
                            value={opt.key}
                            aria-label={opt.title}
                          />
                        </div>

                        <div className="flex flex-col items-start gap-3">
                          <div className="rounded-md bg-gray-100 p-2 text-gray-700">
                            <Icon className="h-6 w-6" aria-hidden />
                          </div>

                          <div className="w-full">
                            <div className="text-md font-medium text-gray-900">
                              {opt.title}
                            </div>
                            <div className="mt-0.5 text-sm text-gray-600">
                              {opt.description}
                            </div>

                            <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600">
                              <Check
                                className="h-3.5 w-3.5 text-gray-500"
                                aria-hidden="true"
                              />
                              <span>{opt.helper}</span>
                            </div>
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </RadioGroup>
              )}
            />

            {errors.choice && (
              <p className="mt-3 text-sm text-red-600">
                {errors.choice.message}
              </p>
            )}
          </CardContent>

          <CardFooter className={cn('flex justify-end gap-3')}>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!isValid}>
              Continue
            </Button>
          </CardFooter>
        </Card>
      </div>
    </form>
  )
}
